import {
  BellIcon,
  ChatCircleIcon,
  CheckIcon,
  CreditCardIcon,
  PlusIcon,
  ShoppingCartIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SendNotificationSheet from '@/components/blocks/sheets/send-notification';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useGlobalState } from '@/contexts/GlobalStateContext';
import { useNotifications } from '@/contexts/NotificationContext';
import {
  ApiNotification,
  formatNotificationTime,
  getNotificationIconType,
} from '@/types/notifications';

type NotificationIconType = 'order' | 'payment' | 'message' | 'alert';

// Moved to a separate component for better organization
const NotificationIcon = ({ type }: { type: NotificationIconType }) => {
  switch (type) {
    case 'order':
      return <ShoppingCartIcon className='h-4 w-4 text-success-70' />;
    case 'payment':
      return <CreditCardIcon className='h-4 w-4 text-brand-60' />;
    case 'message':
      return <ChatCircleIcon className='h-4 w-4 text-indigo-600' />;
    case 'alert':
      return <WarningIcon className='h-4 w-4 text-amber-500' />;
    default:
      return <WarningIcon className='h-4 w-4 text-gray-50' />;
  }
};

// Notification item component for better code organization
const NotificationItem = ({
  notification,
  onRead,
}: {
  notification: ApiNotification;
  onRead: (id: string) => void;
}) => (
  <div
    onClick={() => onRead(notification._id)}
    className={`px-4 py-3 cursor-pointer transition-colors duration-200 ${
      !notification.isRead
        ? 'bg-gray-5 hover:bg-gray-10'
        : 'bg-white hover:bg-gray-5'
    }`}
  >
    <div className='flex items-start gap-3'>
      {!notification.isRead && (
        <span className='h-2 w-2 bg-success-50 rounded-full mt-2'></span>
      )}

      {/* Icon */}
      <div className='flex-shrink-0 p-2 rounded-full bg-gray-10'>
        <NotificationIcon type={getNotificationIconType(notification.type)} />
      </div>

      {/* Title and Message */}
      <div className='flex-1 min-w-0'>
        <div className='flex justify-between items-center mb-1'>
          <p className='text-sm font-semibold text-gray-80'>
            {notification.title}
          </p>
          <span className='text-[11px] text-gray-40 whitespace-nowrap'>
            {formatNotificationTime(notification.createdAt)}
          </span>
        </div>
        <p className='text-xs text-gray-50'>{notification.message}</p>
        {notification.sender && (
          <p className='text-xs text-gray-40 mt-1'>
            From: {notification.sender.username}
          </p>
        )}
      </div>
    </div>
  </div>
);

const NotificationsPopover = () => {
  const navigate = useNavigate();
  const { showSheet } = useGlobalState();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'all' | 'unread'>('all');

  // Use the notification context instead of local state
  const {
    notifications,
    unreadCount,
    isLoading: loading,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  // Convert NotificationItem[] to ApiNotification[] for compatibility
  const apiNotifications: ApiNotification[] = useMemo(
    () =>
      notifications.map(notification => ({
        _id: notification._id,
        title: notification.title,
        message: notification.message,
        type: notification.type,
        isRead: notification.isRead,
        createdAt: notification.createdAt,
        updatedAt: notification.updatedAt,
        sender: {
          _id: 'system',
          email: 'system@veggieworld.africa',
          username: 'System',
        },
      })),
    [notifications]
  );

  const filteredNotifications = useMemo(
    () =>
      tab === 'all'
        ? apiNotifications
        : apiNotifications.filter(n => !n.isRead),
    [tab, apiNotifications]
  );

  // Use useCallback for event handlers
  const markOneAsRead = useCallback(
    async (id: string) => {
      await markAsRead(id);
    },
    [markAsRead]
  );

  // Navigate to notifications page
  const viewAllNotifications = useCallback(() => {
    navigate('/notifications');
    setOpen(false);
  }, [navigate]);

  // Send notification handler
  const sendNotification = useCallback(() => {
    showSheet({
      id: 'send-notification',
      type: 'send-notification',
      title: 'Send Notification to Team',
      content: <SendNotificationSheet />,
    });
    setOpen(false);
  }, [showSheet]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          hierarchy='link'
          size='sm'
          className='relative overflow-visible'
        >
          <BellIcon className='h-5 w-5' />
          {unreadCount > 0 && (
            <span className='absolute -top-1 -right-1 h-4 w-4 bg-destructive-50 rounded-full flex items-center justify-center text-[10px] font-bold text-white z-50'>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      {open && (
        <PopoverContent
          className='w-96 p-0 rounded-lg shadow-lg bg-white'
          align='end'
        >
          {/* Header Section */}
          <div className='flex flex-col'>
            <div className='flex items-center justify-between px-4 pt-4'>
              <h4 className='font-semibold text-lg text-gray-90'>
                Notifications
              </h4>
              <div className='flex items-center gap-2'>
                <Button
                  hierarchy='outlined'
                  size='sm'
                  onClick={sendNotification}
                  className='text-xs h-7 px-2'
                >
                  <PlusIcon className='h-3 w-3 mr-1' />
                  Send
                </Button>
                <Button
                  hierarchy='outlined'
                  size='sm'
                  className='h-6 w-6 p-0 text-gray-50 hover:text-gray-80'
                  onClick={() => setOpen(false)}
                >
                  <XIcon className='h-4 w-4' />
                </Button>
              </div>
            </div>

            {/* Tabs and Action */}
            <div className='flex items-center justify-between border-b mt-4 px-4'>
              {/* Tabs */}
              <div className='flex gap-6 relative'>
                <button
                  onClick={() => setTab('all')}
                  className={`pb-3 text-sm font-medium ${
                    tab === 'all'
                      ? 'text-black border-b-2 border-success-70'
                      : 'text-gray-40 hover:text-black'
                  }`}
                >
                  All
                </button>

                <button
                  onClick={() => setTab('unread')}
                  className={`pb-3 text-sm font-medium flex items-center gap-1 ${
                    tab === 'unread'
                      ? 'text-black border-b-2 border-success-70'
                      : 'text-gray-40 hover:text-black'
                  }`}
                >
                  Unread
                  {unreadCount > 0 && (
                    <span className='text-xs font-semibold bg-success-10 text-success-70 rounded-full px-2 py-0.5'>
                      {unreadCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Mark all as read - Only show if there are unread notifications */}
              {unreadCount > 0 && (
                <Button
                  hierarchy='outlined'
                  size='sm'
                  className='flex items-center gap-1 text-xs text-gray-50 hover:text-black p-0 h-auto'
                  onClick={markAllAsRead}
                >
                  <CheckIcon className='h-3 w-3' />
                  Mark all as read
                </Button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <ScrollArea className='h-[320px]'>
            <div className='divide-y'>
              {loading ? (
                <div className='flex items-center justify-center h-40 text-gray-40 text-sm'>
                  Loading notifications...
                </div>
              ) : filteredNotifications.length > 0 ? (
                filteredNotifications.map(notification => (
                  <NotificationItem
                    key={notification._id}
                    notification={notification}
                    onRead={markOneAsRead}
                  />
                ))
              ) : (
                <div className='flex flex-col items-center justify-center h-40 text-gray-40 text-sm'>
                  <BellIcon className='h-8 w-8 mb-2' />
                  <p>No notifications yet</p>
                  <p className='text-xs'>
                    Your notifications will appear here once you start receiving
                    updates.
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className='p-2 border-t'>
            <Button
              hierarchy='outlined'
              className='w-full text-xs text-success-70 hover:text-black'
              onClick={viewAllNotifications}
            >
              View all notifications
            </Button>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

export default NotificationsPopover;
